import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";

import { getStationDetails } from "../../../services/stations";
import EvseList from "./components/Evse/EvseList";
import OpeningHours from "./components/OpeningHours/OpeningHours";
import MapAndAddress from "./components/MapAndAddress/MapAndAddress";

import "./StationDetails.css";
import { getEvseLabelsByStationId } from "../../../services/clearing/stations";
import CustmerActionButtons from "./components/CustomerActionButtons/CustomerActionButtons";

interface Station {
  id?: string;
  name?: string;
  coordinates?: { lat: number; lng: number };
  address?: string;
  city?: string;
  state?: string;
  opening_times?: any;
  evses?: any[];
}

export default function StationDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);

  const [stationDetails, setStationDetails] = useState<Station>({});

  const fetchStationData = async (stationId: string) => {
    setLoading(true);
    try {
      const [labelsResponse, detailsResponse] = await Promise.all([
        getEvseLabelsByStationId(stationId),
        getStationDetails(stationId),
      ]);

      const extendedStationDetails = {
        ...detailsResponse,
        evses: detailsResponse.evses?.map((evse: any) => ({
          ...evse,
          label: labelsResponse
            ? labelsResponse?.filter((l: any) => l.evseId === evse.evse_id)[0]
                ?.label
            : "",
        })),
      };

      setStationDetails(extendedStationDetails);
    } catch (err) {
      console.error("Station data fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStationData(id);
  }, [id]);

  if (loading) {
    return (
      <div className="card flex justify-content-center mt-6">
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div className="surface-section px-4 py-5 md:px-6 lg:px-8">

      {/* Page header */}
      <div className="flex flex-column md:flex-row justify-content-between align-items-start md:align-items-center gap-3 mb-4">
        <div>
          <div className="text-2xl font-semibold">{stationDetails?.name}</div>
          <div className="text-color-secondary text-sm mt-1">{stationDetails?.id}</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            label="Add Owners"
            icon="pi pi-user-plus"
            outlined
            onClick={() => navigate(`/add-owner/${id}`)}
          />
          <Button
            label="Transactions"
            icon="pi pi-calendar-clock"
            outlined
            onClick={() => navigate(`/transactions/${id}`)}
          />
          <Button
            label="Notification history"
            icon="pi pi-bell"
            outlined
            onClick={() => navigate(`/alerting/notifications/${id}`)}
          />
          <CustmerActionButtons stationId={id} />
        </div>
      </div>

      <div className="grid">

        {/* Location */}
        <div className="col-12 md:col-7">
          <Card title="Location" className="shadow-1 h-full">
            <MapAndAddress
              coordinates={stationDetails?.coordinates}
              address={stationDetails?.address}
              city={stationDetails?.city}
              state={stationDetails?.state}
            />
          </Card>
        </div>

        {/* Opening Hours */}
        <div className="col-12 md:col-5">
          <Card title="Opening Hours" className="shadow-1 h-full">
            <OpeningHours openingTimes={stationDetails?.opening_times} />
          </Card>
        </div>

        {/* EVSEs */}
        <div className="col-12">
          <Card title="EVSEs" className="shadow-1">
            <EvseList
              evses={stationDetails?.evses}
              stationId={stationDetails?.id}
            />
          </Card>
        </div>

      </div>
    </div>
  );
}
